<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from '../services/api'
import ContentTree from '../components/admin/ContentTree.vue'
import SubjectPanel from '../components/admin/SubjectPanel.vue'
import SemesterPanel from '../components/admin/SemesterPanel.vue'
import ChapterPanel from '../components/admin/ChapterPanel.vue'
import LessonPanel from '../components/admin/LessonPanel.vue'

const route = useRoute()
const router = useRouter()
const subjects = ref(null)
const error = ref('')
const toast = ref('')
const panel = ref(null)
let toastTimer
const selected = computed(() => String(route.query.chon || ''))

// Tìm mục đang chọn cùng các cấp cha để hiển thị đúng khung chi tiết.
const context = computed(() => {
  const [kind, rawId] = selected.value.split('-')
  const id = Number(rawId)
  for (const subject of subjects.value || []) {
    if (kind === 'subject' && subject.id === id) return { kind, subject, path: [] }
    for (const semester of subject.semesters) {
      if (kind === 'semester' && semester.id === id) return { kind, subject, semester, path: [`subject-${subject.id}`] }
      for (const chapter of semester.chapters) {
        if (kind === 'chapter' && chapter.id === id) return { kind, subject, semester, chapter, path: [`subject-${subject.id}`, `semester-${semester.id}`] }
        const lesson = kind === 'lesson' && chapter.lessons.find(item => item.id === id)
        if (lesson) return { kind, subject, semester, chapter, lesson, path: [`subject-${subject.id}`, `semester-${semester.id}`, `chapter-${chapter.id}`] }
      }
    }
  }
  return { kind: selected.value === 'new-subject' ? 'new-subject' : '', path: [] }
})

// Lớp là cấp đầu tiên khi soạn: môn thuộc lớp nào thì bài trong môn đó dành cho lớp đó.
const grades = computed(() => [...new Set((subjects.value || []).map(subject => subject.grade))].sort((a, b) => a - b))
const activeGrade = computed(() => context.value.subject?.grade ?? (Number(route.query.lop) || grades.value[0] || 6))
const gradeSubjects = computed(() => (subjects.value || []).filter(subject => subject.grade === activeGrade.value))
const barGrades = computed(() => [...new Set([...grades.value, activeGrade.value])].sort((a, b) => a - b))
const otherGrades = computed(() => Array.from({ length: 12 }, (_, index) => index + 1).filter(grade => !barGrades.value.includes(grade)))

function selectGrade(grade) {
  router.replace({ query: { lop: grade, ...(grades.value.includes(grade) ? {} : { chon: 'new-subject' }) } })
}

async function load() {
  try { subjects.value = (await api('/admin/overview')).subjects; error.value = '' }
  catch (err) { error.value = err.message }
}

async function select(key) {
  await router.replace({ query: { lop: activeGrade.value, chon: key } })
  await nextTick()
  if (window.matchMedia('(max-width: 900px)').matches) panel.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

async function changed({ select: key, message } = {}) {
  await load()
  if (key) await select(key)
  if (message) {
    toast.value = message
    clearTimeout(toastTimer)
    toastTimer = setTimeout(() => toast.value = '', 3000)
  }
}

onMounted(load)
</script>

<template>
  <header class="page-heading-row"><div><p class="eyebrow">Soạn nội dung</p><h1 class="page-title">Chương trình học</h1><p class="page-subtitle">Chọn lớp, rồi chọn môn / chương / bài trên cây để sửa và thêm nội dung con ngay tại chỗ.</p></div><RouterLink to="/quan-tri/nhap-xuat" class="btn btn-light">Nhập / xuất học liệu</RouterLink></header>
  <div v-if="error" class="error-banner" style="margin-top:20px">{{ error }} <button class="btn btn-light" @click="load">Thử lại</button></div>
  <div v-if="!subjects && !error" class="loading-block"><div><div class="loader"></div><p>Đang tải chương trình…</p></div></div>
  <div v-if="subjects" class="grade-bar" role="tablist" aria-label="Chọn lớp">
    <span class="grade-bar-label">Đang soạn cho</span>
    <button v-for="grade in barGrades" :key="grade" type="button" role="tab" :aria-selected="grade === activeGrade" :class="{ active: grade === activeGrade }" @click="selectGrade(grade)">Lớp {{ grade }}<small>{{ subjects.filter(subject => subject.grade === grade).length }} môn</small></button>
    <select class="grade-bar-add" aria-label="Thêm lớp mới" @change="event => { const grade = Number(event.target.value); event.target.value = ''; if (grade) selectGrade(grade) }"><option value="">＋ Lớp khác…</option><option v-for="grade in otherGrades" :key="grade" :value="grade">Lớp {{ grade }}</option></select>
  </div>
  <div v-if="subjects" class="content-workspace">
    <aside class="card content-tree-card"><ContentTree :subjects="gradeSubjects" :grade="activeGrade" :selected="selected" :path="context.path" @select="select" /></aside>
    <section ref="panel" class="card content-panel">
      <nav v-if="context.subject" class="panel-crumbs" aria-label="Vị trí đang soạn">
        <span class="crumb-grade">Lớp {{ context.subject.grade }}</span><span>›</span>
        <button type="button" @click="select(`subject-${context.subject.id}`)">{{ context.subject.title }}</button>
        <template v-if="context.semester"><span>›</span><button type="button" @click="select(`semester-${context.semester.id}`)">{{ context.semester.title }}</button></template>
        <template v-if="context.chapter"><span>›</span><button type="button" @click="select(`chapter-${context.chapter.id}`)">{{ context.chapter.title }}</button></template>
      </nav>
      <LessonPanel v-if="context.kind === 'lesson'" :lesson="context.lesson" :chapter="context.chapter" :subject="context.subject" @changed="changed" @select="select" />
      <ChapterPanel v-else-if="context.kind === 'chapter'" :chapter="context.chapter" :semester="context.semester" :subject="context.subject" @changed="changed" @select="select" />
      <SemesterPanel v-else-if="context.kind === 'semester'" :semester="context.semester" :subject="context.subject" @changed="changed" @select="select" />
      <SubjectPanel v-else-if="context.kind === 'subject' || context.kind === 'new-subject'" :subject="context.subject || null" :default-grade="activeGrade" @changed="changed" @select="select" />
      <div v-else class="panel-empty">
        <span>✎</span>
        <h2>{{ gradeSubjects.length ? `Soạn nội dung lớp ${activeGrade}` : `Lớp ${activeGrade} chưa có môn học` }}</h2>
        <p>{{ gradeSubjects.length ? 'Bấm vào môn, học kỳ, chương hoặc bài ở cây bên trái. Khung này sẽ hiện thông tin để sửa và nút thêm nội dung con.' : 'Tạo môn đầu tiên cho lớp này, sau đó thêm học kỳ, chương và bài học.' }}</p>
        <button type="button" class="btn btn-primary" @click="select('new-subject')">＋ Tạo môn cho lớp {{ activeGrade }}</button>
      </div>
    </section>
  </div>
  <div v-if="toast" class="toast" role="status">✓ {{ toast }}</div>
</template>
